import { useEffect, useState, useCallback } from 'react'
import { Building2, Loader2, AlertTriangle, RefreshCw } from 'lucide-react'
import { type Business } from '@/stores/useMyBusinessStore'
import { useBillingStore } from '@/stores/useBillingStore'
import { isApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/**
 * "Pro · 250 credits" — the per-business essentials for managing several.
 * A business with no subscription row arrives as tier "trial" (the
 * backend's fallback), so this only renders nothing when the billing fields
 * are absent entirely (e.g. a stale cached list).
 */
export function billingSummary(biz: Business): string | null {
  const parts: string[] = []
  if (biz.subscription_tier) {
    parts.push(biz.subscription_tier.charAt(0).toUpperCase() + biz.subscription_tier.slice(1))
  }
  if (biz.credit_balance !== null && biz.credit_balance !== undefined) {
    parts.push(`${Math.floor(biz.credit_balance)} credits`)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

export interface BusinessSelectorProps {
  businesses: Business[]
  activeBusinessId: string | undefined
  isLoading: boolean
  error: string | null
  onSwitch: (id: string) => Promise<void>
  onRetry: () => void
}

export function BusinessSelector({
  businesses,
  activeBusinessId,
  isLoading,
  error,
  onSwitch,
  onRetry,
}: BusinessSelectorProps) {
  const activeBiz = businesses.find((b) => b.id === activeBusinessId) ?? businesses[0]

  if (isLoading && businesses.length === 0) {
    return (
      <div className="bg-muted flex h-9 w-[200px] items-center rounded-md border px-3">
        <Loader2 className="text-muted-foreground size-4 animate-spin" />
      </div>
    )
  }

  // A genuine fetch failure with nothing loaded — distinct from a user who
  // legitimately has exactly one business (that's the branch below). Without
  // this, a failed fetch left `businesses` empty forever and rendered a
  // permanent, unrecoverable "No business".
  if (businesses.length === 0 && error) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="text-destructive h-9"
        data-testid="business-selector-retry"
      >
        <RefreshCw className="size-4" />
        Couldn't load — Retry
      </Button>
    )
  }

  if (businesses.length <= 1) {
    const summary = activeBiz ? billingSummary(activeBiz) : null
    return (
      <div className="bg-muted flex h-9 items-center gap-2 rounded-md border px-3 text-sm">
        <Building2 className="text-muted-foreground size-4" />
        <span>{activeBiz?.name ?? 'No business'}</span>
        {summary && (
          <span
            className="text-muted-foreground text-xs"
            data-testid={activeBiz && `business-billing-${activeBiz.id}`}
          >
            {summary}
          </span>
        )}
      </div>
    )
  }

  return (
    <Select onValueChange={onSwitch} value={activeBiz?.id}>
      <SelectTrigger className="w-[240px]" data-testid="business-selector">
        <SelectValue placeholder="Select business" />
      </SelectTrigger>
      <SelectContent>
        {businesses.map((biz) => {
          const summary = billingSummary(biz)
          return (
            <SelectItem key={biz.id} value={biz.id}>
              <span className="truncate">{biz.name}</span>
              {summary && (
                <span
                  className="text-muted-foreground text-xs"
                  data-testid={`business-billing-${biz.id}`}
                >
                  {summary}
                </span>
              )}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

// ---------------------------------------------------------------------------
// DeleteBusinessDialog
// ---------------------------------------------------------------------------

/** The `code` of a 409 refusal from DELETE /businesses/{id}, if that's what failed. */
function deletionRefusalCode(error: unknown): string | undefined {
  if (!isApiError(error) || error.status !== 409) return undefined
  const code = (error.details as Record<string, unknown> | undefined)?.code
  return typeof code === 'string' ? code : undefined
}

export interface DeleteBusinessDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessName: string
  onConfirm: () => Promise<void>
}

export function DeleteBusinessDialog({
  open,
  onOpenChange,
  businessName,
  onConfirm,
}: DeleteBusinessDialogProps) {
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [billedByStripe, setBilledByStripe] = useState(false)
  const openPortal = useBillingStore((s) => s.openPortal)
  const isPortalLoading = useBillingStore((s) => s.isPortalLoading)
  const isConfirmValid = confirmText.trim() === businessName

  // Reopening (e.g. for a different active business after a switch) must not
  // carry over a confirm string that matched the previous name.
  useEffect(() => {
    if (open) {
      setConfirmText('')
      setBilledByStripe(false)
    }
  }, [open])

  const handleClose = useCallback(() => {
    if (!deleting) onOpenChange(false)
  }, [deleting, onOpenChange])

  const handleConfirm = useCallback(async () => {
    if (!isConfirmValid) return
    setDeleting(true)
    try {
      await onConfirm()
      onOpenChange(false)
    } catch (error) {
      // The caller toasts the reason; the dialog stays open, and a business
      // Stripe still bills gets a way to cancel it from here.
      setBilledByStripe(deletionRefusalCode(error) === 'paid_subscription')
    } finally {
      setDeleting(false)
    }
  }, [isConfirmValid, onConfirm, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="delete-business-dialog">
        <DialogHeader>
          <div className="bg-destructive/10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
            <AlertTriangle className="text-destructive h-6 w-6" />
          </div>
          <DialogTitle className="text-center">Delete business</DialogTitle>
          <DialogDescription className="text-center">
            <strong>{businessName}</strong> will disappear from the app immediately — its data is
            deactivated, not permanently erased. This can't be undone from here.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="delete-business-confirm">
            Type <strong>{businessName}</strong> to confirm
          </Label>
          <Input
            id="delete-business-confirm"
            data-testid="delete-business-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={businessName}
            disabled={deleting}
            className="border-destructive/30 focus-visible:border-destructive focus-visible:ring-destructive"
          />
        </div>

        {billedByStripe && (
          <div
            className="bg-muted space-y-2 rounded-md p-3 text-sm"
            data-testid="delete-business-billing-notice"
          >
            <p>
              <strong>{businessName}</strong> is still on a paid plan. Cancel its subscription in
              billing, then come back and delete it.
            </p>
            <Button
              variant="outline"
              className="min-h-11 w-full"
              onClick={() => void openPortal()}
              disabled={isPortalLoading}
              data-testid="delete-business-manage-billing"
            >
              {isPortalLoading && <Loader2 className="size-4 animate-spin" />}
              Cancel subscription in billing
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isConfirmValid || deleting}
            data-testid="confirm-delete-business-button"
          >
            {deleting && <Loader2 className="size-4 animate-spin" />}
            Delete Business
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
